<template>
  <div class="subscriptions-list">
    <h1
      v-if="!isOnePlanAvailable"
      class="subscriptions-list__title subscriptions-list__title--bold"
    >
      Subscriptions
    </h1>
    <div v-if="!isOnePlanAvailable" class="subscriptions-list__intro">
      Whether for individuals or teams, INE has an IT training solution. Choose from our
      subscription plans and start your upskilling journey.
    </div>
    <div class="subscriptions-list__subscribed" v-if="userHasSubscription && !isBusinessUser">
      <div class="subscriptions-list__subscribed-title">You have an INE subscription</div>
      <div class="subscriptions-list__subscribed-subtitle">Visit my.ine.com to change plans</div>
      <div class="subscriptions-list__subscribed-cta">
        <a :href="streamingLink" class="btn btn--primary"> Change plans </a>
      </div>
    </div>
    <div class="subscriptions-list__subscribed" v-if="isBusinessUser">
      <div class="subscriptions-list__subscribed-title">
        {{ isBusinessPlanExpired ? 'Your Business plan has expired' : 'You have a Business plan' }}
      </div>
      <div v-if="isBusinessPlanExpired" class="subscriptions-list__subscribed-subtitle">
        To receive Business plan options please contact your business account owner
      </div>
    </div>
    <div
      class="subscriptions-list__plans"
      v-if="!userHasSubscription && !isBusinessUser && isOnePlanAvailable"
    >
      <PlanListItemRow :plans="individualPlans" />
    </div>
    <div
      class="subscriptions-list__plans"
      v-if="!userHasSubscription && !isBusinessUser && !isOnePlanAvailable"
    >
      <div class="subscriptions-list__selector">
        <div class="subscriptions-list__selector-wrapper">
          <button
            class="btn btn--responsive btn--bold subscriptions-list__selector-btn"
            :class="
              currentTemplate === 'individuals'
                ? 'btn--primary'
                : 'btn--secondary subscriptions-list__selector-btn--inactive'
            "
            @click="setTemplate('individuals')"
          >
            Individuals
          </button>
          <button
            class="btn btn--responsive btn--bold subscriptions-list__selector-btn"
            :class="
              currentTemplate === 'teams'
                ? 'btn--primary'
                : 'btn--secondary subscriptions-list__selector-btn--inactive'
            "
            @click="setTemplate('teams')"
          >
            Teams
          </button>
        </div>
      </div>
      <transition-group name="fade" tag="div" class="subscriptions-list__products">
        <div
          v-if="currentTemplate === 'individuals'"
          class="subscriptions-list__products-plans-individuals"
          key="individuals-plans"
        >
          <div class="subscriptions-list__products-plans-individuals-row">
            <PlanRowItem
              v-for="plan in individualPlans"
              :key="plan.id"
              :plan="plan"
              class="subscriptions-list__products-plans-individuals-item"
            />
          </div>
        </div>
        <div
          v-if="currentTemplate === 'teams'"
          class="subscriptions-list__products-plans-teams"
          key="teams-plans"
        >
          <PlanListTeamsDetails />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import CONSTANTS from '@/constants'
import { updateCart } from '@/helpers/mixins'
import PlanListItemRow from '@/components/plan-item-card/PlanListItemRow.vue'
import PlanRowItem from '@/components/plan-item-card/PlanRowItem.vue'
import PlanListTeamsDetails from '@/components/plan-item-card/PlanListTeamsDetails.vue'

const { PLANS } = CONSTANTS

export default {
  name: 'SubscriptionsList',
  mixins: [updateCart],
  components: {
    PlanListTeamsDetails,
    PlanListItemRow,
    PlanRowItem,
  },
  data() {
    return {
      currentTemplate: 'individuals',
    }
  },
  created() {
    if (this.subViewSelected && ['individuals', 'teams'].includes(this.subViewSelected)) {
      this.setTemplate(this.subViewSelected)
    }
  },
  computed: {
    ...mapState({
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      availableProductBundles: state => state.products.availableProductBundles,
    }),
    ...mapGetters({
      plans: 'products/plansByTerm',
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
      isJuneSavingsPromoActive: 'app/getIsJuneSavingsPromoActive',
      subViewSelected: 'app/getSubscriptionViewSelected',
    }),
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    individualPlans() {
      if (this.isOnePlanAvailable) {
        return this.plans.yearly.filter(p => [PLANS.BAH].includes(p.merchant_id))
      }
      const annualPlansFilter = [PLANS.ANNUAL, PLANS.PREMIUM]
      const annualPlans = this.plans.yearly
        .filter(p => {
          if (p.merchant_id === PLANS.PREMIUM) {
            p.isBestValue = true
          }
          return annualPlansFilter.includes(p.merchant_id)
        })
        .reverse()
      const monthlyPlans = this.plans.monthly.filter(p => PLANS.MONTHLY === p.merchant_id)
      return [...monthlyPlans, ...annualPlans]
    },
    streamingLink() {
      return `//${process.env.VUE_APP_STREAMING_URL}/dashboard/profile`
    },
  },
  methods: {
    ...mapMutations({
      setIsSubscriptionsTeamsSelected: 'app/IS_SUBSCRIPTIONS_TEAMS_SELECTED_UPDATED',
    }),
    setTemplate(templateToShow) {
      this.setIsSubscriptionsTeamsSelected(templateToShow === 'teams')
      this.currentTemplate = templateToShow
    },
  },
}
</script>

<style lang="scss" scoped>
.subscriptions-list {
  &__title {
    font-weight: 450;
    font-size: 24px;
    line-height: 32px;
    margin: 0;
    margin-bottom: 30px;
    &--bold {
      font-size: rem(24px);
      line-height: rem(32px);
      font-style: normal;
      font-weight: 700;
      margin-bottom: 30px;
    }
  }
  &__intro {
    color: var(--light-blue-gray);
    font-size: rem(18px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(24px);
    margin-bottom: 50px;
  }
  &__subscriptions {
    border-bottom: 1px solid var(--mid-gray-translucid);
    margin-bottom: 60px;
  }
  &__subscribed {
    padding: 40px;
    background: var(--mid-gray-gradient);
    border-radius: 10px;
    color: var(--white);
    font-family: var(--font-regular);
    display: flex;
    justify-content: center;
    flex-flow: column;
    align-items: center;
    margin-bottom: 60px;
    &-title {
      font-weight: 700;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 10px;
      text-align: center;
    }
    &-subtitle {
      font-weight: 450;
      font-size: 16px;
      line-height: 24px;
      margin-bottom: 30px;
      color: var(--dusty-gray);
    }
    &-cta {
      .btn {
        text-decoration: none;
      }
    }
  }
  &__selector {
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
    &-wrapper {
      background: var(--black-1);
      border-radius: 10px;
      border: 2px solid var(--light-purple);
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      &:hover {
        background: var(
          --mid-gray-translucid-gradient-two,
          linear-gradient(140deg, rgba(187, 188, 199, 0.7) 0%, rgba(187, 188, 199, 0) 100%)
        );
      }
      &:active {
        background: var(--black-1);
      }
      @include mobile {
        width: auto;
      }
    }
    &-btn {
      &:active {
        color: var(--white);
        background: none;
        opacity: 1;
      }
      &--inactive {
        opacity: 0.8;
      }
    }
    :global(.btn--secondary) {
      border: none;
    }
    @include tablet {
      :global(.btn) {
        width: 180px;
      }
    }
  }
  &__products-plans {
    &-individuals {
      &-row {
        display: flex;
        flex-flow: column;
        gap: 20px;
        @include tablet-ipad-air-and-mini-portrait {
          gap: 5px;
        }
        @include tablet-ipad-pro {
          gap: 20px;
        }
        @include mobile {
          flex-flow: row;
        }
      }
    }
  }
}
</style>
